<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from './stores/auth';
import { useNotificationsStore } from './stores/notifications';
import { useIdleTimeout } from './composables/useIdleTimeout';
import { useDashboardApi } from './services/dashboardApi';
import { usePerformanceMonitor } from './services/performanceMonitor';
import { storeToRefs } from 'pinia';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const notificationsStore = useNotificationsStore();
const loading = ref(true);

// Initialize idle timeout (4 hours)
const { cleanup: cleanupIdleTimeout } = useIdleTimeout(240);

// Get notification error from store
const { error: notificationError } = storeToRefs(notificationsStore);

onMounted(async () => {
  try {
    if (!authStore.initialized) {
      await authStore.initialize();
    }

    // Initialize notifications after auth is ready and only if authenticated
    if (authStore.isAuthenticated) {
      await notificationsStore.initialize();

      // Initialize performance monitoring
      const performanceMonitor = usePerformanceMonitor();
      performanceMonitor.initialize();

      // Initialize dashboard data preload (non-blocking)
      const dashboardApi = useDashboardApi();
      setTimeout(() => {
        dashboardApi.preloadDashboardData().catch(error => {
          console.warn('[App] Dashboard preload failed (non-critical):', error.message || error);
        });
      }, 1000);
    }
  } catch (error) {
    console.error('[App] Error during initialization:', error);
  } finally {
    loading.value = false;
  }
});

// Clean up when component is unmounted
onUnmounted(() => {
  // Clean up idle timeout
  cleanupIdleTimeout();

  // Clean up auth store event listeners
  authStore.cleanup();

  // Clean up notifications if needed
  if (notificationsStore.isInitialized) {
    notificationsStore.disconnect();
  }
});

// Watch for notification errors
watch(notificationError, (error) => {
  if (error) {
    console.error('[App] Notification system error:', error);
  }
});
</script>

<template>
  <div v-if="!loading">
    <router-view />
  </div>
  <div v-else class="flex h-screen items-center justify-center bg-gray-50 dark:bg-dark-900 transition-colors">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
  </div>
</template>

<style>
@import '@fortawesome/fontawesome-free/css/all.css';
</style>
